import React, { useRef, useState, useEffect, useCallback } from 'react';

import { Box, Heading, Flex } from '@chakra-ui/layout';
import { AutocompleteInput, Unform, TextInput, Button } from 'components';
import { FormHandles, SubmitHandler } from '@unform/core';
import { getClientUsers } from 'services/usersTable';
import { getLoggedUser } from 'utils/storage';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal';
import { colors } from 'theme/colors';
import { Participant } from 'pages';
import { createUser } from 'services/createUser';
import { participantValidationSchema } from 'yupSchemas/participant';
import { openNotification } from 'utils/notification';
import { RiErrorWarningLine as Warning } from 'react-icons/ri';

import ParticipantCard from '../ParticipantCard';
import useEventBus from 'hooks/useEventBus';

interface IProps {
  participants: Array<Participant>;
  setParticipants: React.Dispatch<React.SetStateAction<Array<Participant>>>;
  savedParticipants: Array<Participant>;
  setSavedParticipants: React.Dispatch<React.SetStateAction<Array<Participant>>>;
  setDeletedParticipants: React.Dispatch<React.SetStateAction<Array<Participant>>>;
}

const Participants: React.FC<IProps> = ({
  participants,
  setParticipants,
  savedParticipants,
  setDeletedParticipants,
  setSavedParticipants,
}) => {
  const formRef = useRef<FormHandles>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [clients, setClients] = useState<Participant[]>([]);
  const [isLoading, setIsloading] = useState(false);
  const eventBus = useEventBus();

  useEffect(() => {
    (async () => {
      const client = getLoggedUser().client;
      if (client) {
        const { data } = await getClientUsers(client);
        setClients(data);
      }
    })();
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSubmit = useCallback(
    async (
      data: SubmitHandler<{
        name: string;
        jobTitle: string;
        email: string;
        department: string;
        username: string;
      }>,
    ) => {
      setIsloading(true);
      try {
        const { data: dt } = await createUser({
          ...data,
          employeeDanielLaw: false,
          temporaryAccess: false,
          // @ts-ignore
          client: {
            // @ts-ignore
            id: +(getLoggedUser().client || '0'),
            name: getLoggedUser().clientName,
          },
          // @ts-ignore
          roles: [{ id: 23 }],
        });
        // @ts-ignore
        const client = { name: dt.name, email: dt.email, id: dt.id || 0 };
        setParticipants(p => [...p, client]);
        setIsOpen(false);
      } catch (err) {}
      setIsloading(false);
    },
    [],
  );

  const handleRemovePar = useCallback((p: Participant, type?: string | any) => {
    if (type === 'is-saved') {
      setSavedParticipants(ps => {
        const arr = [...ps];
        const index = arr.findIndex(i => i.id === p.id);
        if (index > -1) setDeletedParticipants(p => [...p, arr[index]]);
        if (index > -1) arr.splice(index, 1);
        return arr;
      });
    } else {
      setParticipants(ps => {
        const arr = [...ps];
        const index = arr.findIndex(i => i.id === p.id);
        if (index > -1) arr.splice(index, 1);
        return arr;
      });
    }
  }, []);

  return (
    <Box mb="16px" mt="10px">
      <Heading mb="14px" as="h4" fontSize="14px" fontWeight="bold">
        Adicionar participantes
      </Heading>
      <Unform formRef={formRef}>
        <AutocompleteInput
          name="participant"
          action={{
            name: 'Cadastrar',
            cb: () => {
              setIsOpen(true);
            },
          }}
          leftIcon="search"
          options={clients}
          optionLabel="name"
          optionValue="id"
          onSelectOption={e => {
            setTimeout(() => formRef.current?.clearField('participant'), 500);
            const part = clients.find(i => i.id === +e.optionValue);
            if (
              part &&
              participants.findIndex(i => i.id === part.id) === -1 &&
              savedParticipants.findIndex(i => i.name === part.name && i.email === part.email) ===
                -1
            ) {
              if (!part.enabled)
                eventBus.dispatchEvent('onPopup', {
                  text: 'Este usuário está inativo. Para poder adicioná-lo como participante, antes você precisará reativá-lo.',
                });
              else setParticipants(p => [...p, part]);
            } else if (!part) {
              // null;
            } else openNotification('Participante já adicionado', 'error');
          }}
        />
      </Unform>
      <Modal isOpen={isOpen} onClose={handleCloseModal}>
        <ModalOverlay />
        <ModalContent alignSelf="center" maxW="700px" padding="24px">
          <Unform
            onSubmit={handleSubmit}
            formRef={formRef}
            validationSchema={participantValidationSchema}
          >
            <ModalHeader color={colors.brand.gray600}>Cadastrar Usuário</ModalHeader>
            <ModalCloseButton
              color="white"
              background={'#737D86'}
              clipPath={'circle()'}
              top={'45px'}
              right={'45px'}
              _hover={{ background: '#9ca9b4' }}
              width="22px"
              fontSize={'8px'}
              fontWeight={700}
            />
            <ModalBody>
              <Flex gridGap="10px" flexDirection="column">
                <Flex gridGap="20px" flexDirection="row">
                  <Flex flex={1}>
                    <TextInput label="Usuário" name="username" placeholder="Digite aqui" />
                  </Flex>
                  <Flex flex={1}>
                    <TextInput label="Nome" name="name" placeholder="Digite aqui" />
                  </Flex>
                  <Flex flex={1}>
                    <TextInput
                      label="E-mail"
                      type="email"
                      name="email"
                      placeholder="exemplo@email.com"
                    />
                  </Flex>
                </Flex>
                <Flex gridGap="20px" flexDirection="row">
                  <Flex flex={1}>
                    <TextInput label="Cargo" name="jobTitle" placeholder="Digite aqui" />
                  </Flex>
                  <Flex flex={1}>
                    <TextInput label="Departamento" name="department" placeholder="Digite aqui" />
                  </Flex>
                </Flex>
              </Flex>
              <Box
                display="flex"
                gridGap="15px"
                width="100%"
                borderRadius="8px"
                p="16px"
                bg={colors.brand[3000]}
                alignItems="flex-start"
                mt="15px"
                mb="20px"
                border={'2px solid red'}
              >
                <div>
                  <Warning color={'red'} fontSize="2rem" />
                </div>
                <Box p="0" fontWeight={'500'} fontSize="14px" color={colors.brand[1400]} as="p">
                  Você está criando um usuário temporário e este terá acesso somente às informações
                  deste documento em específico até a data de sua finalização.
                </Box>
              </Box>
            </ModalBody>

            <ModalFooter display={'flex'} justifyContent={'space-between'}>
              <Box marginRight="1rem">
                <Button
                  style={{
                    border: `2px solid ${colors.brand.gray300}`,
                    color: ` ${colors.brand.gray300}`,
                  }}
                  variant="cancel"
                  type="button"
                  disabled={isLoading}
                  onClick={handleCloseModal}
                >
                  Cancelar
                </Button>
              </Box>
              <Box>
                <Button
                  type="button"
                  onClick={() => formRef.current?.submitForm()}
                  isLoading={isLoading}
                >
                  Salvar
                </Button>
              </Box>
            </ModalFooter>
          </Unform>
        </ModalContent>
      </Modal>
      <Flex flexDirection="column" width="100%">
        {savedParticipants.map(i => (
          <ParticipantCard
            key={i.id}
            name={i.name}
            email={i.email}
            onRemove={handleRemovePar.bind(null, i, 'is-saved')}
            enabled={i.enabled}
          />
        ))}
        {participants.map(i => (
          <ParticipantCard
            key={i.id}
            name={i.name}
            email={i.email}
            onRemove={handleRemovePar.bind(null, i)}
            enabled={i.enabled}
          />
        ))}
      </Flex>
    </Box>
  );
};

export default Participants;
